import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, FileText, Download } from "lucide-react";
import api, { formatMoney } from "@/lib/api";

const CHANNELS = ["plataforma", "instagram", "directo", "otro"];

export default function AdminVentas() {
    const [leads, setLeads] = useState([]);
    const [sel, setSel] = useState(null);
    const [payments, setPayments] = useState([]);
    const [sale, setSale] = useState({ sale_amount: "", sale_channel: "plataforma", sale_date: "", sale_notes: "" });
    const [pay, setPay] = useState({ amount: "", date: "", note: "" });
    const [tpl, setTpl] = useState("");
    const [manual, setManual] = useState(null);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/leads");
            setLeads(data.filter((l) => l.status !== "borrador"));
        } catch (err) { /* noop */ }
    }, []);
    useEffect(() => {
        load();
        api.get("/admin/contract-template").then((r) => setTpl(r.data.body || "")).catch(() => {});
    }, [load]);

    const openLead = async (l) => {
        setSel(l);
        const s = l.sale || {};
        setSale({
            sale_amount: s.sale_amount ?? (l.total || ""),
            sale_channel: s.sale_channel || "plataforma",
            sale_date: s.sale_date || "",
            sale_notes: s.sale_notes || "",
        });
        try { const { data } = await api.get(`/admin/leads/${l.id}/payments`); setPayments(data); }
        catch (_e) { setPayments([]); }
    };

    const saveSale = async (isSale = true) => {
        try {
            const { data } = await api.put(`/admin/leads/${sel.id}/sale`, {
                is_sale: isSale,
                sale_amount: Number(sale.sale_amount) || 0,
                sale_channel: sale.sale_channel,
                sale_date: sale.sale_date || null,
                sale_notes: sale.sale_notes,
            });
            setSel(data);
            toast.success(isSale ? "Venta registrada" : "Venta desmarcada");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar la venta"); }
    };

    const addPayment = async () => {
        if (!Number(pay.amount)) { toast.error("Ingresa el monto del abono"); return; }
        try {
            const { data } = await api.post(`/admin/leads/${sel.id}/payments`, {
                amount: Number(pay.amount), date: pay.date || null, note: pay.note,
            });
            setPayments((p) => [...p, data]);
            setPay({ amount: "", date: "", note: "" });
            toast.success("Abono registrado");
        } catch (_e) { toast.error("Error al registrar abono"); }
    };

    const delPayment = async (id) => {
        await api.delete(`/admin/payments/${id}`);
        setPayments((p) => p.filter((x) => x.id !== id));
    };

    const uploadContract = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            await api.post(`/admin/leads/${sel.id}/contract`, form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Contrato subido");
            const { data } = await api.get("/admin/leads");
            const updated = data.find((l) => l.id === sel.id);
            if (updated) setSel(updated);
            load();
        } catch (_e) { toast.error("Error al subir el contrato"); }
        finally { setUploading(false); }
    };

    const downloadContract = async () => {
        try {
            const resp = await api.get(`/admin/leads/${sel.id}/contract`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = sel.contract?.filename || "contrato";
            a.click();
            URL.revokeObjectURL(url);
        } catch (_e) { toast.error("No se pudo descargar"); }
    };

    const generateContract = () => {
        const carpa = sel.carpa || {};
        const vars = {
            fecha_hoy: new Date().toLocaleDateString("es-EC"),
            nombre: sel.contact_name || "",
            ci_ruc: "________",
            fecha_evento: sel.exact_date || "por definir",
            invitados: String(sel.guests ?? ""),
            valor: formatMoney(Number(sale.sale_amount) || sel.total || 0),
            valor_letras: "________",
            precio_extra_pax: "15",
            estructura: carpa.width ? "SI" : "NO",
            m2_estructura: carpa.area_m2 ? String(carpa.area_m2) : "0",
            carpas_servicio: "0",
            m2_piso: "0",
        };
        let body = tpl;
        Object.entries(vars).forEach(([k, v]) => { body = body.replaceAll(`{{${k}}}`, v); });
        const w = window.open("", "_blank");
        w.document.write(`<html><head><title>Acuerdo — ${vars.nombre}</title>
            <style>body{font-family:Georgia,serif;max-width:760px;margin:40px auto;color:#1b2a3d;line-height:1.65;white-space:pre-wrap;font-size:12.5px;padding:0 20px}</style>
            </head><body>${body.replaceAll("\n", "<br/>")}</body></html>`);
        w.document.close();
        setTimeout(() => w.print(), 400);
    };

    const registerManual = async () => {
        if (!manual.name || !manual.amount) { toast.error("Nombre y monto son requeridos"); return; }
        try {
            const { data: lead } = await api.post("/leads", {
                contact_name: manual.name,
                event_type: manual.event_type || "Boda",
                email: manual.email || null,
                phone: manual.phone || null,
                guests: Number(manual.guests) || 0,
                date_mode: "exact",
                exact_date: manual.event_date || null,
                tentative_months: [],
                location: "", locations: [], providers: {}, tech_services: [], extra_services: {},
                channel: "admin",
                breakdown: {},
                total: Number(manual.amount) || 0,
                session_quote_id: `manual-${Date.now()}`,
                accepted_terms: true,
                internal_quote: true,
            });
            await api.put(`/admin/leads/${lead.id}/sale`, {
                is_sale: true,
                sale_amount: Number(manual.amount),
                sale_channel: manual.channel || "directo",
                sale_date: manual.sale_date || null,
                sale_notes: manual.notes || "Venta registrada manualmente fuera de plataforma",
            });
            toast.success("Venta manual registrada");
            setManual(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Error al registrar venta manual"); }
    };

    const totalPaid = useMemo(() => payments.reduce((s, p) => s + (p.amount || 0), 0), [payments]);

    return (
        <div className="space-y-4" data-testid="admin-ventas">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-xs text-muted-foreground max-w-xl">
                    Registra ventas cerradas (dentro o fuera de la plataforma), sube contratos y controla los abonos/ingresos por evento. Solo ingresos — los costos de proveedores quedan para la fase 2.
                </p>
                <Button onClick={() => setManual({ event_type: "Boda", channel: "directo" })} className="rounded-none bg-accent text-accent-foreground" data-testid="btn-manual-sale">
                    + Registrar venta manual
                </Button>
            </div>

            {manual && (
                <div className="bg-white border-2 border-accent p-6" data-testid="manual-sale-form">
                    <h3 className="font-display text-2xl mb-3">Venta fuera de plataforma</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <F label="Cliente *"><Input value={manual.name || ""} onChange={(e) => setManual({ ...manual, name: e.target.value })} className="rounded-none" data-testid="ms-name" /></F>
                        <F label="Monto venta (USD) *"><Input type="number" value={manual.amount || ""} onChange={(e) => setManual({ ...manual, amount: e.target.value })} className="rounded-none tabular" data-testid="ms-amount" /></F>
                        <F label="Canal">
                            <select value={manual.channel} onChange={(e) => setManual({ ...manual, channel: e.target.value })} className="w-full border border-border rounded-none px-2 py-2 bg-white text-sm">
                                {CHANNELS.map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </F>
                        <F label="Fecha del evento"><Input type="date" value={manual.event_date || ""} onChange={(e) => setManual({ ...manual, event_date: e.target.value })} className="rounded-none" /></F>
                        <F label="Email"><Input value={manual.email || ""} onChange={(e) => setManual({ ...manual, email: e.target.value })} className="rounded-none" /></F>
                        <F label="Teléfono"><Input value={manual.phone || ""} onChange={(e) => setManual({ ...manual, phone: e.target.value })} className="rounded-none" /></F>
                        <F label="Invitados"><Input type="number" value={manual.guests || ""} onChange={(e) => setManual({ ...manual, guests: e.target.value })} className="rounded-none tabular" /></F>
                        <F label="Notas"><Input value={manual.notes || ""} onChange={(e) => setManual({ ...manual, notes: e.target.value })} className="rounded-none" /></F>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <Button onClick={registerManual} className="rounded-none bg-primary text-primary-foreground" data-testid="ms-save">Registrar venta</Button>
                        <Button variant="ghost" onClick={() => setManual(null)} className="rounded-none">Cancelar</Button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white border border-border p-4 max-h-[560px] overflow-y-auto">
                    <div className="overline text-muted-foreground mb-2">Cotizaciones / eventos</div>
                    {leads.map((l) => (
                        <button
                            key={l.id}
                            type="button"
                            onClick={() => openLead(l)}
                            className={`w-full text-left border-b border-border py-2.5 px-2 text-xs hover:bg-secondary/50 ${sel?.id === l.id ? "bg-secondary" : ""}`}
                            data-testid={`sale-lead-${l.id}`}
                        >
                            <div className="flex justify-between items-center">
                                <span className="font-medium">{l.contact_name}</span>
                                {l.sale?.is_sale
                                    ? <span className="bg-eucalyptus/20 text-eucalyptus px-1.5 py-0.5 text-[9px] overline">Venta</span>
                                    : <span className="text-muted-foreground text-[9px] overline">Lead</span>}
                            </div>
                            <div className="text-muted-foreground mt-0.5">{l.event_type || "Boda"} · {(l.created_at || "").slice(0, 10)} · <span className="tabular">{l.total != null ? formatMoney(l.total) : "—"}</span></div>
                        </button>
                    ))}
                    {leads.length === 0 && <div className="overline text-muted-foreground py-6 text-center">Sin cotizaciones</div>}
                </div>

                <div className="lg:col-span-2 bg-white border border-border p-6">
                    {!sel ? (
                        <div className="overline text-muted-foreground">Selecciona una cotización para gestionar la venta.</div>
                    ) : (
                        <div className="space-y-6" data-testid="sale-panel">
                            <div className="flex items-baseline justify-between flex-wrap gap-2">
                                <h3 className="font-display text-2xl">{sel.contact_name}</h3>
                                <span className="text-xs text-muted-foreground">{sel.event_type || "Boda"} · {sel.guests ?? "—"} pax · cotizado {sel.total != null ? formatMoney(sel.total) : "—"}</span>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <F label="Monto de venta (USD)"><Input type="number" value={sale.sale_amount} onChange={(e) => setSale({ ...sale, sale_amount: e.target.value })} className="rounded-none tabular" data-testid="sale-amount" /></F>
                                <F label="Canal de origen">
                                    <select value={sale.sale_channel} onChange={(e) => setSale({ ...sale, sale_channel: e.target.value })} className="w-full border border-border rounded-none px-2 py-2 bg-white text-sm" data-testid="sale-channel">
                                        {CHANNELS.map((c) => <option key={c}>{c}</option>)}
                                    </select>
                                </F>
                                <F label="Fecha de venta"><Input type="date" value={sale.sale_date} onChange={(e) => setSale({ ...sale, sale_date: e.target.value })} className="rounded-none" data-testid="sale-date" /></F>
                                <F label="Notas"><Input value={sale.sale_notes} onChange={(e) => setSale({ ...sale, sale_notes: e.target.value })} className="rounded-none" /></F>
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={() => saveSale(true)} className="rounded-none bg-eucalyptus text-white hover:opacity-90" data-testid="btn-mark-sale">
                                    {sel.sale?.is_sale ? "Actualizar venta" : "✓ Marcar como venta"}
                                </Button>
                                {sel.sale?.is_sale && (
                                    <Button variant="ghost" onClick={() => saveSale(false)} className="rounded-none text-destructive" data-testid="btn-unmark-sale">Desmarcar</Button>
                                )}
                            </div>

                            {/* Abonos */}
                            <div className="border-t border-border pt-4">
                                <div className="flex items-baseline justify-between">
                                    <div className="overline text-muted-foreground">Abonos / ingresos</div>
                                    <div className="text-xs tabular">Recibido: <strong>{formatMoney(totalPaid)}</strong>{Number(sale.sale_amount) > 0 && <span className="text-muted-foreground"> / {formatMoney(Number(sale.sale_amount))} · saldo {formatMoney(Number(sale.sale_amount) - totalPaid)}</span>}</div>
                                </div>
                                <div className="mt-3 space-y-1.5">
                                    {payments.map((p) => (
                                        <div key={p.id} className="flex items-center justify-between text-xs border border-border px-3 py-2">
                                            <span><span className="tabular font-medium">{formatMoney(p.amount)}</span> · {p.date} {p.note && <span className="text-muted-foreground">— {p.note}</span>}</span>
                                            <button onClick={() => delPayment(p.id)} className="text-destructive" data-testid={`del-payment-${p.id}`}><Trash2 className="h-3 w-3" /></button>
                                        </div>
                                    ))}
                                </div>
                                <div className="grid grid-cols-[120px_150px_1fr_auto] gap-2 mt-3">
                                    <Input type="number" placeholder="Monto" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="rounded-none tabular text-xs" data-testid="pay-amount" />
                                    <Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className="rounded-none text-xs" data-testid="pay-date" />
                                    <Input placeholder="Nota (ej. anticipo 30%)" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} className="rounded-none text-xs" data-testid="pay-note" />
                                    <Button size="sm" onClick={addPayment} className="rounded-none bg-primary text-primary-foreground" data-testid="btn-add-payment">+ Abono</Button>
                                </div>
                            </div>

                            {/* Contrato */}
                            <div className="border-t border-border pt-4">
                                <div className="overline text-muted-foreground mb-2">Contrato</div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <label className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs cursor-pointer hover:border-foreground bg-white">
                                        <FileText className="h-3.5 w-3.5" /> {uploading ? "Subiendo…" : "Subir contrato firmado"}
                                        <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" onChange={(e) => uploadContract(e.target.files?.[0])} data-testid="contract-file-input" />
                                    </label>
                                    {sel.contract && (
                                        <button onClick={downloadContract} className="inline-flex items-center gap-1.5 text-xs underline-editorial" data-testid="btn-download-contract">
                                            <Download className="h-3.5 w-3.5" /> {sel.contract.filename}
                                        </button>
                                    )}
                                    <Button size="sm" variant="outline" onClick={generateContract} className="rounded-none" data-testid="btn-generate-contract">
                                        Generar contrato desde plantilla
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Plantilla de contrato: se gestiona en la pestaña Contratos */}
            <p className="text-[11px] text-muted-foreground" data-testid="template-hint">
                La plantilla estándar del contrato y el envío por email para firma se gestionan en la pestaña <strong>Contratos</strong>.
            </p>
        </div>
    );
}

function F({ label, children }) {
    return (
        <div>
            <Label className="overline text-muted-foreground text-[10px]">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
